<template>
  <div class="gs" ref="root">
    <!-- Input -->
    <div class="gs-input" :class="{ 'is-open': isOpen }">
      <i class="fa-solid fa-magnifying-glass gs-input__icon"></i>
      <input
          ref="input"
          type="text"
          class="gs-input__field"
          v-model="searchTerm"
          :placeholder="$t('globalSearch.placeholder')"
          autocomplete="off"
          @input="onInput"
          @focus="onFocus"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="selectActive"
          @keydown.esc.prevent="close"
      />
      <b-spinner v-if="loading" small class="gs-input__spinner"/>
      <button v-else-if="searchTerm" type="button" class="gs-input__clear" @click="clear">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <!-- Results panel -->
    <div v-if="isOpen" class="gs-panel">
      <template v-if="searchTerm.length < 2">
        <div class="gs-hint">{{ $t('globalSearch.typeMore') }}</div>
      </template>

      <template v-else-if="!loading && !clients.length && !accounts.length">
        <div class="gs-hint">
          <i class="fa-regular fa-face-frown mr-50"></i>{{ $t('globalSearch.noResults') }}
        </div>
      </template>

      <template v-else>
        <!-- Clients -->
        <div v-if="clients.length" class="gs-section">
          <div class="gs-section__title">
            <i class="fa-solid fa-users"></i>{{ $t('globalSearch.clients') }}
          </div>
          <button
              v-for="(client, i) in clients" :key="'c' + client.id"
              type="button" class="gs-row"
              :class="{ 'is-active': activeIndex === i }"
              @mouseenter="activeIndex = i"
              @mousedown.prevent="openClient(client.id)"
          >
            <span class="gs-row__avatar">{{ initials(client) }}</span>
            <span class="gs-row__main">
              <span class="gs-row__name">
                <span v-html="highlight(fullName(client))"></span>
                <country-flag v-if="client.country" :country="client.country" size="small" class="gs-row__flag"/>
              </span>
              <span class="gs-row__meta">
                <span v-if="hasPermission(permissions.CLIENT_READ_EMAIL)" v-html="highlight(client.email)"></span>
                <span v-else>xxxx@xxx.com</span>
                <template v-if="client.phone"> · <span v-html="highlight(client.phone)"></span></template>
              </span>
            </span>
            <span class="gs-row__id">#<span v-html="highlight(String(client.id))"></span></span>
          </button>
        </div>

        <!-- Accounts -->
        <div v-if="accounts.length" class="gs-section">
          <div class="gs-section__title">
            <i class="fa-solid fa-wallet"></i>{{ $t('globalSearch.accounts') }}
          </div>
          <button
              v-for="(account, i) in accounts" :key="'a' + account.id"
              type="button" class="gs-row"
              :class="{ 'is-active': activeIndex === clients.length + i }"
              @mouseenter="activeIndex = clients.length + i"
              @mousedown.prevent="openClient(account.clientId)"
          >
            <span class="gs-row__avatar gs-row__avatar--account"><i class="fa-solid fa-wallet"></i></span>
            <span class="gs-row__main">
              <span class="gs-row__name">
                #<span v-html="highlight(String(account.id))"></span>
                <span class="gs-pill" :class="account.isDemo ? 'gs-pill--demo' : 'gs-pill--real'">
                  {{ account.isDemo ? 'DEMO' : 'REAL' }}
                </span>
                <span v-if="account.active === false" class="gs-pill gs-pill--inactive">
                  {{ $t('columns.inactive') }}
                </span>
              </span>
              <span class="gs-row__meta">
                {{ $t('columns.balance') }}: {{ (account.balance || 0) | numberFormatter }} {{ account.currency || '' }}
              </span>
            </span>
            <span class="gs-row__id">{{ $t('columns.clientId') }} {{ account.clientId }}</span>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import _debounce from 'lodash/debounce'
import axios from 'axios'
import CountryFlag from 'vue-country-flag'
import { mapActions } from 'vuex'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import mixinBase from '@/components/mixin/mixinBase'
import { vue } from '@/main'

export default {
  name: 'searchbar',
  components: { CountryFlag },
  mixins: [mixinBase, mixinPermissions],

  data() {
    return {
      searchTerm: '',
      clients: [],
      accounts: [],
      loading: false,
      isOpen: false,
      activeIndex: -1,
      requestSeq: 0, // stale-response guard: only the LAST fired search may land
      // The client search is a heavy unanchored regex over 8 fields: every
      // keystroke used to leave the previous request running. Six of them in
      // flight exhaust the browser's per-host connection limit and EVERY
      // other API call stalls until they finish ("search dead until refresh").
      // Each new search now cancels the previous one.
      cancelSource: null,
    }
  },

  mounted() {
    document.addEventListener('mousedown', this.onOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutsideClick)
    this.cancelInflight()
  },

  methods: {
    ...mapActions('clients', ['getAllClientsSearch']),

    onInput() {
      this.isOpen = true
      this.loading = this.searchTerm.length >= 2
      this.debouncedSearch()
    },
    onFocus() {
      if (this.searchTerm.length >= 2 && (this.clients.length || this.accounts.length)) {
        this.isOpen = true
      }
    },
    onOutsideClick(event) {
      if (!this.$refs.root || this.$refs.root.contains(event.target)) return
      this.isOpen = false
    },
    close() {
      this.isOpen = false
      this.$refs.input && this.$refs.input.blur()
    },
    clear() {
      this.cancelInflight()
      this.searchTerm = ''
      this.clients = []
      this.accounts = []
      this.activeIndex = -1
      this.loading = false
      this.isOpen = false
    },
    cancelInflight() {
      if (this.cancelSource) {
        this.cancelSource.cancel('superseded')
        this.cancelSource = null
      }
    },

    debouncedSearch: _debounce(function () {
      this.search()
    }, 300),

    search() {
      const term = this.searchTerm.trim()
      this.cancelInflight()
      if (term.length < 2) {
        this.clients = []
        this.accounts = []
        this.loading = false
        return
      }
      const seq = ++this.requestSeq
      this.loading = true
      const source = axios.CancelToken.source()
      this.cancelSource = source
      const cancelToken = source.token

      // Clients: the backend free-text search already covers name, email,
      // phone, mobile, username, ACCOUNT NUMBER and client id — no client-side
      // re-filtering (the old re-filter silently hid valid matches, e.g. hits
      // on username or account number).
      const clientsRequest = this.getAllClientsSearch({
        filter: {
          search: term,
          pagination: { page: 1, size: 8 },
          sort: { direction: 'DESC', field: 'creationTime' },
        },
        cancelToken,
      }).then(res => res.data || [], () => [])

      // Account numbers: the free-text search above is a "contains" match over
      // phone/mobile/id too, so a 6-digit account number was drowned by
      // clients whose PHONE contains those digits (only 8 rows come back).
      // A numeric term therefore also searches the clients' accounts ONLY —
      // those owners go first. Uses the client endpoint (CLIENT_READ), so
      // agents without MONETARY_READ_ALL find accounts too.
      const numeric = /^\d{3,}$/.test(term)
      const byAccountRequest = numeric
          ? this.getAllClientsSearch({
            filter: {
              account: term,
              pagination: { page: 1, size: 5 },
              sort: { direction: 'DESC', field: 'creationTime' },
            },
            cancelToken,
          }).then(res => res.data || [], () => [])
          : Promise.resolve([])

      // No /account/get/all call any more: it hung the whole search (spinner
      // forever) while both client searches had long answered. The accounts
      // section is built from the account owners' embedded accounts instead —
      // same id/balance/currency/demo/active fields, same visibility rules.

      // Never spin forever: whatever happens to the requests, stop after 15s.
      const watchdog = setTimeout(() => {
        if (seq === this.requestSeq && this.loading) {
          this.cancelInflight()
          this.loading = false
        }
      }, 15000)

      Promise.all([clientsRequest, byAccountRequest]).then(([found, byAccount]) => {
        clearTimeout(watchdog)
        if (seq !== this.requestSeq) return // a newer search already fired (or this one was cancelled)
        if (this.cancelSource === source) this.cancelSource = null
        // account owners first, then the free-text hits (no duplicates)
        const seen = new Set()
        const clients = [...byAccount, ...found]
            .filter(c => c && !seen.has(c.id) && seen.add(c.id))
            .slice(0, 8)
        // accounts section: the matching accounts embedded in their owners
        const accounts = []
        const known = new Set()
        byAccount.forEach(c => (c.accounts || []).forEach(a => {
          const id = String(a.id)
          if (!id.includes(term) || known.has(id)) return
          known.add(id)
          accounts.push({ ...a, id, clientId: c.id })
        }))
        // the exact account number on top
        accounts.sort((a, b) => (String(b.id) === term) - (String(a.id) === term))
        accounts.splice(5)
        this.clients = clients
        this.accounts = accounts
        this.activeIndex = (clients.length || accounts.length) ? 0 : -1
        this.loading = false
        this.isOpen = true
      }).catch(err => {
        // a rendering error must not leave the spinner on
        clearTimeout(watchdog)
        console.error('global search', err)
        if (seq === this.requestSeq) this.loading = false
      })
    },

    // ---- keyboard navigation over the flattened clients+accounts list ----
    totalRows() {
      return this.clients.length + this.accounts.length
    },
    move(step) {
      const total = this.totalRows()
      if (!total) return
      this.isOpen = true
      this.activeIndex = (this.activeIndex + step + total) % total
    },
    selectActive() {
      if (this.activeIndex < 0) return
      if (this.activeIndex < this.clients.length) {
        this.openClient(this.clients[this.activeIndex].id)
      } else {
        const account = this.accounts[this.activeIndex - this.clients.length]
        if (account) this.openClient(account.clientId)
      }
    },

    openClient(clientId) {
      if (!clientId) return
      this.clear()
      setTimeout(() => vue.$emit('open-client-modal', clientId), 100)
    },

    fullName(client) {
      return [client.firstName, client.lastName].filter(Boolean).join(' ') || client.id
    },
    initials(client) {
      const first = (client.firstName || ' ')[0] || ''
      const last = (client.lastName || ' ')[0] || ''
      return (first + last).toUpperCase().trim() || '?'
    },
    highlight(text) {
      if (text == null || text === '') return ''
      const safe = String(text)
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const term = this.searchTerm.trim()
      if (!term) return safe
      // escape regex specials — typing "(" must not throw
      const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return safe.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>')
    },
  },
}
</script>

<style scoped lang="scss">
.gs {
  position: relative;
  width: 100%;
  max-width: 420px;
}

/* ---- Input ---- */
.gs-input {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 10px 0 12px;
  background: #f4f7fb;
  border: 1px solid #dfe4ec;
  border-radius: 10px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &.is-open,
  &:focus-within {
    background: #ffffff;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
  }

  &__icon { color: #94a3b8; font-size: 0.85rem; flex-shrink: 0; }

  &__field {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.9rem;
    color: #1f2a44;

    &::placeholder { color: #a6b0c3; }
  }

  &__spinner { color: #007bff; flex-shrink: 0; }

  &__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    flex-shrink: 0;

    &:hover { background: rgba(234, 84, 85, 0.12); color: #ea5455; }
  }
}

/* ---- Panel ---- */
.gs-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 1050;
  max-height: 420px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid rgba(110, 117, 159, 0.18);
  border-radius: 14px;
  box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35), 0 4px 12px rgba(20, 24, 50, 0.08);
  padding: 6px;
}

.gs-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.4rem 1rem;
  font-size: 0.85rem;
  color: #94a3b8;
}

.gs-section {
  & + & { margin-top: 4px; border-top: 1px solid rgba(110, 117, 159, 0.12); padding-top: 4px; }

  &__title {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px 4px;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #94a3b8;

    i { color: #007bff; font-size: 0.7rem; }
  }
}

/* ---- Rows ---- */
.gs-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &.is-active { background: rgba(0, 123, 255, 0.08); }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 9px;
    background: linear-gradient(135deg, #6da7ff 0%, #007bff 100%);
    color: #fff;
    font-size: 0.72rem;
    font-weight: 800;

    &--account { background: linear-gradient(135deg, #9e95f5 0%, #7367f0 100%); font-size: 0.8rem; }
  }

  &__main { flex: 1; min-width: 0; }

  &__name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #1f2a44;
    white-space: nowrap;
    overflow: hidden;
  }

  &__flag { flex-shrink: 0; transform: scale(0.42); margin: -8px; }

  &__meta {
    display: block;
    font-size: 0.74rem;
    color: #8a93a8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__id {
    flex-shrink: 0;
    font-size: 0.72rem;
    font-weight: 700;
    color: #007bff;
    white-space: nowrap;
  }

  ::v-deep mark {
    background: rgba(255, 200, 60, 0.45);
    color: inherit;
    padding: 0 1px;
    border-radius: 3px;
  }
}

.gs-pill {
  font-size: 0.6rem;
  font-weight: 800;
  padding: 1px 7px;
  border-radius: 999px;
  letter-spacing: 0.5px;

  &--real { background: #dcfce7; color: #166534; }
  &--demo { background: #fef3c7; color: #92400e; }
  &--inactive { background: #fde8ea; color: #dc3545; }
}

/* ---- Dark mode ---- */
.dark-layout {
  .gs-input {
    background: #1f242e;
    border-color: #3d4452;

    &.is-open,
    &:focus-within {
      background: #283046;
      border-color: #4d80b8;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.18);
    }

    &__field { color: #e2e6ee; &::placeholder { color: #5c6680; } }
  }

  .gs-panel {
    background: #283046;
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 18px 40px -16px rgba(0, 0, 0, 0.65);
  }

  .gs-row {
    &.is-active { background: rgba(0, 123, 255, 0.16); }
    &__name { color: #e2e6ee; }
    &__meta { color: #98a0b3; }

    ::v-deep mark { background: rgba(255, 200, 60, 0.3); color: #ffe9ad; }
  }

  .gs-pill--real { background: rgba(34, 197, 94, 0.18); color: #6dd29a; }
  .gs-pill--demo { background: rgba(251, 191, 36, 0.18); color: #fcd34d; }
  .gs-pill--inactive { background: rgba(239, 68, 68, 0.18); color: #ff8a95; }
}
</style>
